/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { HorizontalBarChart } from '@fluentui/react-charting';

import { unitValueMap } from '../../../shared/helper/scale-unit';
import { formatBigDecimal } from '../../../shared/helper/format-big-decimal';

const formatBigNumber = (value, i18n) => {
  if (value === 0) {
    return i18n.getString(_TL_('Very narrow'));
  }
  return formatBigDecimal(value, i18n);
};

const KEY = 'reach-formatEstimation-breakdown';

const formatEstimation = (data) => {
  const estimationAudienceUnit = _.get(data, ['EstAudienceSize', 'Unit']) || 'None';
  return unitValueMap[estimationAudienceUnit].value * (_.get(data, ['EstAudienceSize', 'Value']) || 0);
};

const useStyles = makeStyles({
  chartContainer: {
    paddingBottom: tokens.spacingVerticalMNudge,
  },
});

export const ReachEstimationBreakdown = React.memo(({
  ageOnlyEstimation,
  genderOnlyEstimation,
  locationOnlyEstimation,
  audienceOnlyEstimation,
  interestOnlyEstimation,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useStyles();
  const [openItems, setOpenItems] = React.useState([KEY]);
  const handleToggle = (_event, data) => {
    setOpenItems(data.openItems);
  };
  const estimations = [];
  const ageEstimation = formatEstimation(ageOnlyEstimation);
  const genderEstimation = formatEstimation(genderOnlyEstimation);
  const locationEstimation = formatEstimation(locationOnlyEstimation);
  const audienceEstimation = formatEstimation(audienceOnlyEstimation);
  const interestEstimation = formatEstimation(interestOnlyEstimation);

  if (ageOnlyEstimation !== null) {
    estimations.push({
      label: i18n.getString(_TL_('Age')),
      value: ageEstimation,
      yAxisCalloutData: formatBigNumber(ageEstimation, i18n),
    });
  }
  if (genderOnlyEstimation !== null) {
    estimations.push({
      label: i18n.getString(_TL_('Gender')),
      value: genderEstimation,
      yAxisCalloutData: formatBigNumber(genderEstimation, i18n),
    });
  }
  if (locationOnlyEstimation !== null) {
    estimations.push({
      label: i18n.getString(_TL_('Location')),
      value: locationEstimation,
      yAxisCalloutData: formatBigNumber(locationEstimation, i18n),
    });
  }
  if (audienceOnlyEstimation !== null) {
    estimations.push({
      label: i18n.getString(_TL_('Audience lists')),
      value: audienceEstimation,
      yAxisCalloutData: formatBigNumber(audienceEstimation, i18n),
    });
  }
  if (interestOnlyEstimation !== null) {
    estimations.push({
      label: i18n.getString(_TL_('Interest')),
      value: interestEstimation,
      yAxisCalloutData: formatBigNumber(interestEstimation, i18n),
    });
  }

  estimations.sort((a, b) => b.value - a.value);

  const data = estimations.map(estimation => ({
    chartTitle: estimation.label,
    chartData: [
      {
        legend: estimation.label,
        horizontalBarChartdata: { x: estimation.value, y: estimations[0].value },
        yAxisCalloutData: estimation.yAxisCalloutData,
        color: '#C1423F',
      },
    ],
  }));

  if (!data.length) {
    return null;
  }

  return (
    <Accordion
      openItems={openItems}
      onToggle={handleToggle}
      multiple
      collapsible
    >
      <AccordionItem value={KEY}>
        <AccordionHeader>{i18n.getString(_TL_('Included audiences'))}</AccordionHeader>
        <AccordionPanel>
          <HorizontalBarChart
            data={data}
            className={classes.chartContainer}
            variant="absolute-scale"
            barChartCustomData={({ chartData }) => {
              const dataPoint = chartData && chartData[0];
              const yAxisCalloutData = dataPoint && dataPoint.yAxisCalloutData;
              return (
                <div>
                  {yAxisCalloutData}
                </div>
              );
            }}
          />
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
});

ReachEstimationBreakdown.displayName = 'ReachEstimationBreakdown';
ReachEstimationBreakdown.propTypes = {
  ageOnlyEstimation: PropTypes.shape({
    EstAudienceSize: PropTypes.shape({
      Value: PropTypes.number,
      Unit: PropTypes.string,
    }),
  }),
  genderOnlyEstimation: PropTypes.shape({
    EstAudienceSize: PropTypes.shape({
      Value: PropTypes.number,
      Unit: PropTypes.string,
    }),
  }),
  locationOnlyEstimation: PropTypes.shape({
    EstAudienceSize: PropTypes.shape({
      Value: PropTypes.number,
      Unit: PropTypes.string,
    }),
  }),
  audienceOnlyEstimation: PropTypes.shape({
    EstAudienceSize: PropTypes.shape({
      Value: PropTypes.number,
      Unit: PropTypes.string,
    }),
  }),
  interestOnlyEstimation: PropTypes.shape({
    EstAudienceSize: PropTypes.shape({
      Value: PropTypes.number,
      Unit: PropTypes.string,
    }),
  }),
};

ReachEstimationBreakdown.defaultProps = {
  ageOnlyEstimation: null,
  genderOnlyEstimation: null,
  locationOnlyEstimation: null,
  audienceOnlyEstimation: null,
  interestOnlyEstimation: null,
};
